import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DialogDefaultV2 } from '../../../../components/v2';
import SimilarityModal from './SimilarityModal';

const STATUS = {
  VALID: 'VALID',
  DUPLICATE: 'DUPLICATE',
  ERROR: 'ERROR',
  DUPLICATED_ERROR: 'DUPLICATED_ERROR',
};
const SELECTABLE_STATUSES = [STATUS.VALID, STATUS.DUPLICATE];

const FILTERS = [
  { key: 'all', label: summary => `Todos (${summary.total})` },
  { key: STATUS.VALID, label: summary => `${summary.valid} válidos`, icon: <CheckCircleOutlineIcon sx={{ color: '#4caf50' }} /> },
  { key: STATUS.DUPLICATE, label: summary => `${summary.duplicate} similares`, icon: <WarningIcon sx={{ color: '#ff9800' }} /> },
  { key: STATUS.ERROR, label: summary => `${summary.error} erros`, icon: <ErrorOutlineIcon sx={{ color: '#f44336' }} /> },
];

const StatusIcon = ({ status }) => {
  if (status === STATUS.VALID) return <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 18 }} />;
  if (status === STATUS.DUPLICATE) return <WarningIcon sx={{ color: 'warning.main', fontSize: 18 }} />;
  return <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18 }} />;
};

StatusIcon.propTypes = {
  status: PropTypes.string.isRequired,
};

const ImportValidationModal = ({
  open,
  preview,
  onCancel,
  onConfirm,
  onNewImport,
  importedFileName,
  onDownloadImportedFile,
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [similarityRow, setSimilarityRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const rows = useMemo(() => (preview && Array.isArray(preview.rows) ? preview.rows : []), [preview]);
  const summary = preview ? preview.summary : { total: 0, valid: 0, duplicate: 0, error: 0 };
  const rowsWithIndex = useMemo(() => rows.map((row, rowIndex) => ({ row, rowIndex })), [rows]);

  const getRowKey = (row, idx) => `${row.line}-${idx}`;

  useEffect(() => {
    if (!open) return;
    if (rowsWithIndex.length > 0) {
      const defaultSelected = new Set();
      rowsWithIndex.forEach(({ row, rowIndex }) => {
        if (row.status === STATUS.VALID) {
          defaultSelected.add(getRowKey(row, rowIndex));
        }
      });
      setSelectedRows(defaultSelected);
      return;
    }
    setSelectedRows(new Set());
  }, [open, rowsWithIndex]);

  const filteredRows = useMemo(() => {
    if (activeFilter === 'all') return rowsWithIndex;
    return rowsWithIndex.filter(({ row }) => row.status === activeFilter);
  }, [rowsWithIndex, activeFilter]);

  const selectedLines = useMemo(
    () =>
      rowsWithIndex
        .filter(({ row, rowIndex }) =>
          SELECTABLE_STATUSES.includes(row.status) && selectedRows.has(getRowKey(row, rowIndex)))
        .map(({ row }) => Number(row.line))
        .filter(Number.isInteger),
    [rowsWithIndex, selectedRows],
  );

  const importCount = selectedLines.length;
  const confirmLabel = `Importar ${importCount} NSIR${importCount !== 1 ? 's' : ''}`;

  const handleRowSelect = (rowKey) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowKey)) {
      newSelected.delete(rowKey);
    } else {
      newSelected.add(rowKey);
    }
    setSelectedRows(newSelected);
  };

  return (
    <>
      <DialogDefaultV2
        open={open}
        title="Revisão de Importação"
        handlerCancel={onCancel}
        handlerConfirm={() => onConfirm(selectedLines)}
        confirmMessage={confirmLabel}
        disabledConfirmButton={selectedLines.length === 0}
        cancelMessage="Cancelar"
        fullWidth
        maxWidth="lg"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
            {FILTERS.map(f => (
              <Chip
                key={f.key}
                icon={f.icon}
                label={f.label(summary)}
                onClick={() => setActiveFilter(f.key)}
                variant={activeFilter === f.key ? 'filled' : 'outlined'}
                color={activeFilter === f.key ? 'primary' : 'default'}
                sx={{ fontWeight: 600, fontSize: 13 }}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {onNewImport && (
              <Button variant="contained" color="primary" onClick={onNewImport}>
                Importar planilha
              </Button>
            )}
            {(importedFileName || onDownloadImportedFile) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                {onDownloadImportedFile && (
                  <Button variant="outlined" color="primary" onClick={onDownloadImportedFile}>
                    Baixar planilha importada
                  </Button>
                )}
                {importedFileName && (
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    {importedFileName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            maxHeight: 420,
            overflowY: 'auto',
            overflowX: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }} />
                <TableCell>Linha</TableCell>
                <TableCell>NSIR</TableCell>
                <TableCell>Equipamento</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Detalhes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map(({ row, rowIndex }, visibleIndex) => {
                const rowKey = getRowKey(row, rowIndex);
                const isSelected = selectedRows.has(rowKey);
                return (
                  <TableRow
                    key={rowKey}
                    hover
                    sx={{ bgcolor: visibleIndex % 2 === 0 ? 'background.paper' : 'grey.50' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleRowSelect(rowKey)}
                        size="small"
                        disabled={row.status === STATUS.ERROR || row.status === STATUS.DUPLICATED_ERROR}
                      />
                    </TableCell>
                    <TableCell>{`${row.line} (${row.isEdit ? 'Edição' : 'Novo'})`}</TableCell>
                    <TableCell>{row.nsir || '-'}</TableCell>
                    <TableCell>{row.equipamento || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StatusIcon status={row.status} />
                        <Typography variant="caption">
                          {row.status === STATUS.VALID && 'Válido'}
                          {row.status === STATUS.DUPLICATE && 'Similaridade'}
                          {row.status === STATUS.ERROR && (<> Error <br /> (Informações incorretas) </>)}
                          {row.status === STATUS.DUPLICATED_ERROR && (<> Duplicado <br /> (NSIR e Equipamento) </>)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.status === STATUS.DUPLICATED_ERROR && (
                        <Button size="small" color="primary" onClick={() => setSimilarityRow(row)}>
                          Ver informações
                        </Button>
                      )}
                      {row.status === STATUS.DUPLICATE && (
                        <Button size="small" color="primary" onClick={() => setSimilarityRow(row)}>
                          Ver similares
                        </Button>
                      )}
                      {row.status === STATUS.ERROR && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                          {(row.errors || []).map((err, i) => (
                            <Chip
                              key={i}
                              size="small"
                              label={err}
                              sx={{
                                m: 0.25,
                                maxWidth: 280,
                                height: 'auto',
                                bgcolor: '#ffebee',
                                color: '#c62828',
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  display: 'block',
                                  py: 0.5,
                                  px: 1,
                                },
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {summary.error > 0 && (
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'error.dark', fontSize: 12 }}>
            *** Linhas com erro não podem ser processadas. É necessário atualizar a planilha e realizar uma nova importação
          </Typography>
        )}
      </DialogDefaultV2>

      <SimilarityModal
        open={!!similarityRow}
        results={similarityRow ? (similarityRow.similar || []) : []}
        onCancel={() => setSimilarityRow(null)}
        onProceed={() => setSimilarityRow(null)}
        informationalOnly
      />
    </>
  );
};

ImportValidationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  preview: PropTypes.shape({
    summary: PropTypes.shape({
      total: PropTypes.number,
      valid: PropTypes.number,
      duplicate: PropTypes.number,
      error: PropTypes.number,
    }),
    rows: PropTypes.arrayOf(PropTypes.shape({
      line: PropTypes.number,
      nsir: PropTypes.string,
      equipamento: PropTypes.string,
      status: PropTypes.oneOf(['VALID', 'DUPLICATE', 'ERROR', 'DUPLICATED_ERROR']),
      errors: PropTypes.arrayOf(PropTypes.string),
      similar: PropTypes.array,
      isEdit: PropTypes.bool,
    })),
    previewToken: PropTypes.string,
  }),
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onNewImport: PropTypes.func,
  importedFileName: PropTypes.string,
  onDownloadImportedFile: PropTypes.func,
};

export default ImportValidationModal;
